import React, { useEffect, useRef } from 'react';

import useEventTracking from '@hooks/useEventTracking';
import { TRINITY_PLAYER_API_KEY } from '@src/env';
import { TrackingLocation } from '@tracking';
import { trackTrinityAudioPlayerAnalytics } from '@utils/trinityAudio/analytics';
import { subscribeTrinityInjectOnWindowLoad } from '@utils/trinityAudio/injectTiming';
import {
  parseTrinityTtsMessage,
  shouldDeliverTrinityEventToConsumer,
} from '@utils/trinityAudio/postMessage';
import type { TrinityAudioEventDetail, TrinityAudioEventName } from '@utils/trinityAudio/types';

export type { TrinityAudioEventDetail, TrinityAudioEventName } from '@utils/trinityAudio/types';

export type TrinityDocumentLoadType =
  | 'default'
  | 'onload'
  | 'onSelectorVisible'
  | 'onSelectorExists';

export const TRINITY_CLIPPERABLE_CONTENT_SELECTOR = '.clipperable-content';

export function encodeTrinityDocumentLoadTypeSelector(cssSelector: string): string {
  const trimmed = cssSelector.trim();
  if (!trimmed) return '';
  try {
    return btoa(unescape(encodeURIComponent(trimmed)));
  } catch {
    return '';
  }
}

const TRINITY_API_URL = 'https://trinitymedia.ai/player/trinity/';

function applyTrinityDocumentLoadParams(
  url: URL,
  documentLoadType: TrinityDocumentLoadType,
  documentLoadTypeSelector?: string
): void {
  if (documentLoadType === 'default') return;

  if (documentLoadType === 'onload') {
    url.searchParams.set('documentLoadType', 'onload');
    return;
  }

  const encoded = encodeTrinityDocumentLoadTypeSelector(documentLoadTypeSelector ?? '');
  if (!encoded) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        'TrinityAudioPlayer: documentLoadTypeSelector is required when documentLoadType is onSelectorVisible or onSelectorExists'
      );
    }
    return;
  }

  url.searchParams.set('documentLoadType', documentLoadType);
  url.searchParams.set('documentLoadTypeSelector', encoded);
  console.log('documentLoadTypeSelector', documentLoadTypeSelector);
  console.log('documentLoadType', documentLoadType);
  console.log('url', url.toString());
  console.log('decoded', decodeURIComponent(encoded));
}

type TrinityAudioPlayerProps = {
  /**
   * Called when Trinity posts a TTS message. Use `track` to limit which logical events invoke this.
   * Compare `detail.event` to `TrinityAudioEventName` or inspect `detail.action` (SDK constants).
   */
  onTrinityEvent?: (detail: TrinityAudioEventDetail) => void;
  /**
   * If set, `onTrinityEvent` runs only for these events (after `window.TRINITY_PLAYER.message` is available).
   * Omit to receive every Trinity TTS message for this page.
   */
  track?: readonly TrinityAudioEventName[];
  /**
   * When true (default), the Trinity script is injected only after `window` `load` so the document and
   * its subresources are ready. Set false to inject as soon as the component mounts (e.g. tests).
   * Client-fetched article HTML may still arrive after `load`; gate rendering this component on data if needed.
   */
  injectAfterPageLoad?: boolean;
  /**
   * Trinity `documentLoadType`: when the player reads the page relative to load / a CSS selector.
   * Use `onSelectorVisible` or `onSelectorExists` with dynamic (AJAX) body text.
   */
  documentLoadType?: TrinityDocumentLoadType;
  /**
   * CSS selector for `onSelectorVisible` / `onSelectorExists`. Omit for `default` / `onload`.
   */
  documentLoadTypeSelector?: string;
};

const TrinityAudioPlayer = ({
  onTrinityEvent,
  track,
  injectAfterPageLoad = true,
  documentLoadType = 'default',
  documentLoadTypeSelector,
}: TrinityAudioPlayerProps) => {
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.TrinityAudio,
  });
  const containerRef = useRef(null);
  const onTrinityEventRef = useRef(onTrinityEvent);
  const trackRef = useRef(track);
  const trackEventRef = useRef(trackEvent);

  onTrinityEventRef.current = onTrinityEvent;
  trackRef.current = track;
  trackEventRef.current = trackEvent;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const apiKey = TRINITY_PLAYER_API_KEY?.trim();
    if (!apiKey) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          'TrinityAudioPlayer: TRINITY_PLAYER_API_KEY is missing; skip script injection.'
        );
      }
      return undefined;
    }

    let cancelled = false;
    let script: HTMLScriptElement | null = null;

    const inject = () => {
      if (cancelled) return;
      let scriptUrl: string;
      try {
        const url = new URL(`${TRINITY_API_URL}${apiKey}/`);
        url.searchParams.set('pageURL', window.location.href);
        applyTrinityDocumentLoadParams(url, documentLoadType, documentLoadTypeSelector);
        scriptUrl = url.toString();
      } catch {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('TrinityAudioPlayer: invalid script URL (check TRINITY_PLAYER_API_KEY).');
        }
        return;
      }

      const el = document.createElement('script');
      el.setAttribute('fetchpriority', 'high');
      el.setAttribute('charset', 'UTF-8');
      el.src = scriptUrl;

      container.appendChild(el);
      script = el;
    };

    const removeLoadListener = subscribeTrinityInjectOnWindowLoad(inject, injectAfterPageLoad);

    return () => {
      cancelled = true;
      removeLoadListener?.();
      if (script?.parentNode === container) {
        container.removeChild(script);
      }
    };
  }, [injectAfterPageLoad, documentLoadType, documentLoadTypeSelector]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const parsed = parseTrinityTtsMessage(event);
      if (!parsed) return;

      const { detail, trinity } = parsed;
      trackTrinityAudioPlayerAnalytics(detail.event, trackEventRef.current);

      if (!shouldDeliverTrinityEventToConsumer(trackRef.current, detail.action, trinity)) {
        return;
      }

      onTrinityEventRef.current?.(detail);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <div
      ref={containerRef}
      className="mb-56 max-w-full rounded-20 border border-solid border-grayscale-charcoal p-8 sm:max-w-[500px]"
    >
      <div
        className="trinity-tts-pb"
        dir="ltr"
        style={{
          font: '12px / 18px Verdana, Arial',
          height: '80px',
          lineHeight: '80px',
          textAlign: 'left',
          margin: '0 0 0 82px',
        }}
      >
        <strong style={{ fontWeight: 400 }}>
          Getting your{' '}
          <a
            href="https://trinityaudio.ai"
            style={{
              color: '#4b4a4a',
              textDecoration: 'none',
              fontWeight: 700,
            }}
          >
            Trinity Audio
          </a>{' '}
          player ready…
        </strong>
      </div>
    </div>
  );
};

export default TrinityAudioPlayer;
